import type { CrudFormProps } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import type { OperatorFormState } from './OperatorsFormState'

import React, { useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useFetchSingleOperator } from '@@partner:graphql/query/employees/singleOperator.gql'
import { useMutateSaveOperator } from '@@partner:graphql/mutation/employees/saveOperator.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  notifyOk,
  notifyError,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'
import {
  makeOperatorFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './OperatorsFormState'

import {
  Checkbox,
  Form,
  SelectPicker,
  Panel,
  Message,
  Grid,
  Row,
  Col
} from 'rsuite'
import CustomerPhone from '@components/order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'

import { OperatorFormValidationModel } from '../validation/OperatorFormValidationModel'

import { EMPLOYEE_STATUS_LABELS } from '@@partner:config/employee'
import FormCheckbox from '@components/common/form/FormCheckbox'

export interface OperatorFormProps extends CrudFormProps {
  id?: GQL_ID
}

export default function OperatorForm ({ id, onSaved }: OperatorFormProps) {
  const {
    data,
    loading: loadingOperator,
    error
  } = useFetchSingleOperator({ id })
  const [ saveOperator, { loading: loadingSaveOperator } ] =
    useMutateSaveOperator()

  const {
    formState,
    setFormState,
    setFormRef,
    handleFormChange,
    handleFormError
  } = useValidatableForm<OperatorFormState>(makeOperatorFormState)

  useEffect(() => {
    if (!loadingOperator && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.operator))
      )
    }
  }, [ data, loadingOperator, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveOperator({
          variables: {
            operatorInfo: mapStateToApiVariable(formState),
            upsert: !id
          }
        })
          .then(({ data }) => {
            notifyOk('Данные супервайзера сохранёны')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.operator)
            )
            onSaved?.(data?.operator.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, id, onSaved, saveOperator, setFormState ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки курьера"
      />
    )
  }

  return (
    <div className="CouriersForm">
      <Form
        model={OperatorFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        onError={handleFormError}
        layout="horizontal"
      >
        <h3 className="u-form-heading">Оператор</h3>

        <Grid>
          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.ControlLabel>Фамилия</Form.ControlLabel>
                <Form.Control name="firstName" placeholder="Фамилия" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Имя</Form.ControlLabel>
                <Form.Control name="lastName" placeholder="Имя" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>E-mail</Form.ControlLabel>
                <Form.Control name="email" placeholder="E-mail" />
              </Form.Group>
              {!id ? (
                <Form.Group>
                  <Form.ControlLabel>Пароль</Form.ControlLabel>
                  <Form.Control
                    name="password"
                    placeholder="Пароль"
                    type="password"
                  />
                </Form.Group>
              ) : null}
              <Form.Group>
                <CustomerPhone name="phone" label="Телефон" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Статус</Form.ControlLabel>
                <Form.Control
                  name="status"
                  accepter={SelectPicker}
                  data={EMPLOYEE_STATUS_LABELS}
                  searchable={false}
                  cleanable={false}
                />
              </Form.Group>
            </Col>

            {!!id ? (
              <Col md={12}>
                <Message
                  className="p-4 max-w-lg"
                  type="error"
                  header="Опасные действия"
                  showIcon
                >
                  <Form.Group>
                    <Form.ControlLabel>Изменить пароль</Form.ControlLabel>
                    <FormCheckbox.Control
                      name="changePassword"
                      placeholder="Изменить пароль"
                    />
                    {formState.changePassword ? (
                      <>
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="password"
                          placeholder="Пароль"
                        />
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="repeatPassword"
                          placeholder="Повторите"
                        />
                      </>
                    ) : null}
                  </Form.Group>
                </Message>
              </Col>
            ) : null}
          </Row>
        </Grid>

        <SaveButton
          loading={loadingOperator || loadingSaveOperator}
          disabled={loadingOperator || loadingSaveOperator}
        >
          Сохранить
        </SaveButton>
      </Form>
    </div>
  )
}
